/* ─── IgSafetySummaryCard ────────────────────────────────────────────────────
 * "Safety Score" section (Figma 2071-36136): four KPI progress rings —
 * Posts analyzed, Posts Flagged, Visual risk rate, Comment toxicity — each a
 * small coloured ring (value centred) + label + sublabel. Renders nothing when
 * there's no data. */

interface IgSummary {
  creator_safety_score?: number;
  avg_post_score?: number;
  worst_post_score?: number;
  posts_analyzed?: number;
  posts_attempted?: number;
  posts_flagged?: number;
  visual_risk_rate?: number;
  comment_toxicity_avg?: number;
}

/** Small circular progress ring with the value centred. */
function Ring({ pct, color, value, size = 56 }: { pct: number; color: string; value: string; size?: number }) {
  const stroke = 5;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, pct));
  const dash = `${(p / 100) * circ} ${circ}`;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EDEDED" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={dash}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-[13px] font-semibold text-[#171717]">
        {value}
      </div>
    </div>
  );
}

function RingKpi({
  pct,
  color,
  value,
  label,
  sublabel,
  className = '',
}: {
  pct: number;
  color: string;
  value: string;
  label: string;
  sublabel: string;
  className?: string;
}) {
  return (
    <div className={`flex flex-1 items-center gap-3 ${className}`}>
      <Ring pct={pct} color={color} value={value} />
      <div className="min-w-0">
        <div className="text-body-sm-semibold text-foreground">{label}</div>
        <div className="text-caption-regular text-muted-foreground">{sublabel}</div>
      </div>
    </div>
  );
}

export function IgSafetySummaryCard({ summary }: { summary: IgSummary }) {
  const attempted = summary.posts_attempted ?? 0;
  const analyzed = summary.posts_analyzed ?? 0;
  const flagged = summary.posts_flagged ?? 0;
  const visualRisk = (summary.visual_risk_rate ?? 0) * 100;
  const toxicity = summary.comment_toxicity_avg ?? 0;

  const kpis = [
    (summary.posts_analyzed != null || summary.posts_attempted != null) && {
      pct: attempted > 0 ? (analyzed / attempted) * 100 : 0,
      color: '#047857',
      value: `${analyzed}/${attempted}`,
      label: 'Posts analyzed',
      sublabel: 'All posts scanned',
    },
    summary.posts_flagged != null && {
      pct: attempted > 0 ? (flagged / attempted) * 100 : 0,
      color: flagged > 0 ? '#DC2626' : '#047857',
      value: String(flagged),
      label: 'Posts Flagged',
      sublabel: `Of ${attempted} posts`,
    },
    summary.visual_risk_rate != null && {
      pct: visualRisk,
      color: '#EA580C',
      value: `${visualRisk.toFixed(0)}%`,
      label: 'Visual risk rate',
      sublabel: visualRisk > 0 ? 'Visual risks detected' : 'No visual risks found',
    },
    summary.comment_toxicity_avg != null && {
      pct: toxicity,
      color: '#DC2626',
      value: `${toxicity.toFixed(0)}%`,
      label: 'Comment toxicity',
      sublabel: 'Avg across comments',
    },
  ].filter(Boolean) as Array<{ pct: number; color: string; value: string; label: string; sublabel: string }>;

  if (kpis.length === 0) return null;

  return (
    <div className="flex flex-col gap-5 rounded-2xl border border-border bg-card p-6">
      <div className="text-heading-h6 text-foreground">Safety Score</div>
      <div className="flex flex-wrap items-center gap-y-4 divide-x divide-border">
        {kpis.map((k, i) => (
          <RingKpi
            key={k.label}
            pct={k.pct}
            color={k.color}
            value={k.value}
            label={k.label}
            sublabel={k.sublabel}
            className={i === 0 ? 'pr-4' : 'px-4'}
          />
        ))}
      </div>
    </div>
  );
}
